import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { PAYMENT_METHODS } from "@rentorng/core";
import { api } from "@/lib/api";
import { ExportButton } from "@/components/export-button";
import { Pagination, SearchBox } from "@/components/list-toolbar";
import { useListParams, type PageMeta } from "@/lib/list-params";
import { useFormat } from "@/lib/format";
import { useSession } from "@/lib/session";
import { useActiveModule, useModuleLink } from "@/lib/module-rail";
import { Badge, Button, Card, EmptyState, ErrorState, Input, LinkButton, PageHeader, Select, Spinner, Table } from "@/components/ui";

interface Row {
  id: string;
  receiptNumber: string;
  amount: number;
  method: string;
  status: string;
  receivedOn: string;
  reference: string | null;
  unallocated: number;
  partyId: string;
  partyName: string;
  recordedByName: string | null;
}

export function PaymentsPage() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") ?? "";
  const method = params.get("method") ?? "";
  const from = params.get("from") ?? "";
  const to = params.get("to") ?? "";
  const { can, modules } = useSession();
  /*
    **Declared payments is letting's screen, and Payments is on both rails.**

    A declared payment is a tenant saying they have paid an invoice — `/claims` sits behind
    `requireModule("letting")` on the server for exactly that reason, and every row on that screen
    links to an invoice. Offered from the development rail it was a door out of the module: the
    developer pressed it, met a list of letting invoices, and one more press had moved them. There
    is no development equivalent; a buyer's instalment is declared against their purchase.
  */
  const active = useActiveModule(modules);
  const link = useModuleLink(modules);
  const fmt = useFormat();

  const list = useListParams();

  /**
   * How many declarations are waiting.
   *
   * On the button rather than the sidebar: it is a queue that is usually empty, and a permanent
   * nav item with a zero on it teaches people to ignore it.
   */
  const { data: pending } = useQuery({
    queryKey: ["claims", "pending", "count"],
    queryFn: () => api.get<{ claims: unknown[] }>("/claims", { status: "pending" }),
    /* Not asked at all outside letting. `/claims` is behind `requireModule("letting")`, so a
       development-only agency was answering a 402 to a request whose only purpose is a number on a
       button that is no longer drawn. */
    enabled: active === "letting" && modules.includes("letting"),
  });
  const declared = pending?.claims.length ?? 0;

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["payments", list.q, method, from, to, list.page],
    queryFn: () =>
      api.get<{ payments: Row[]; page: PageMeta }>("/payments", {
        q: list.q,
        method,
        from,
        to,
        page: list.page,
      }),
    placeholderData: (previous) => previous,
  });

  const rows = data?.payments ?? [];
  const total = rows.filter((row) => row.status === "confirmed").reduce((sum, row) => sum + row.amount, 0);

  return (
    <div>
      <PageHeader
        title="Payments"
        subtitle={`${rows.length} shown · ${fmt.money(total)} received`}
        description={"Money in, allocated to the oldest debt first. Record a payment, reverse one that bounced, print a receipt, or review the transfers tenants say they have made."}
        actions={
          <>
            <ExportButton kind="payments" />
            {active === "letting" ? (
              <LinkButton to={link("/app/payments/declared")} variant="secondary">
                Declared{declared ? ` (${declared})` : ""}
              </LinkButton>
            ) : null}
            {can("payment.record") ? <LinkButton to={link("/app/payments/new")} need="payment.record">Record a payment</LinkButton> : null}
          </>
        }
      />

      <Card padded={false}>
        <div className="border-b border-ink-200 px-3 pt-3">
          <SearchBox params={list} placeholder="Receipt, client or reference…" />
        </div>

        <form
          className="flex flex-wrap gap-2 border-b border-ink-200 p-3"
          onSubmit={(event) => {
            event.preventDefault();
            const form = new FormData(event.currentTarget);
            const next: Record<string, string> = {};
            if (list.q) next.q = list.q;
            for (const key of ["method", "from", "to"]) {
              const value = String(form.get(key) ?? "");
              if (value) next[key] = value;
            }
            setParams(next);
          }}
        >
          <Select name="method" defaultValue={method} className="max-w-40">
            <option value="">Any method</option>
            {PAYMENT_METHODS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </Select>
          <Input name="from" type="date" defaultValue={from} className="max-w-40" />
          <Input name="to" type="date" defaultValue={to} className="max-w-40" />
          <Button type="submit" variant="secondary">Filter</Button>
          {method || from || to ? <Button variant="ghost" onClick={() => setParams({})}>Clear</Button> : null}
        </form>

        {isLoading ? (
          <Spinner />
        ) : error ? (
          <ErrorState error={error} retry={() => refetch()} />
        ) : rows.length === 0 ? (
          <EmptyState
            title="No payments here."
            hint="Record cash, a transfer or a POS receipt — allocation across invoices is automatic."
            action={can("payment.record") ? <LinkButton to="/app/payments/new" need="payment.record">Record a payment</LinkButton> : undefined}
          />
        ) : (
          <Table
            head={
              <tr>
                <th>Receipt</th>
                <th>From</th>
                <th>Received</th>
                <th>Method</th>
                <th>Reference</th>
                <th>Recorded by</th>
                <th className="num">Amount</th>
              </tr>
            }
          >
            {rows.map((row) => (
              <tr key={row.id}>
                <td>
                  {/* A gap, not a space: JSX drops the whitespace between two elements on
                      separate lines, so the badge sat against the receipt number. */}
                  <span className="inline-flex items-center gap-2">
                    <Link to={`/app/payments/${row.id}`} className="link">{row.receiptNumber}</Link>
                    {row.status === "reversed" ? <Badge tone="danger">reversed</Badge> : null}
                  </span>
                </td>
                <td><Link to={`/app/clients/${row.partyId}`} className="text-ink-700 hover:underline">{row.partyName}</Link></td>
                <td className="text-ink-500">{fmt.date(row.receivedOn)}</td>
                <td className="capitalize text-ink-500">{row.method.replace(/_/g, " ")}</td>
                <td className="text-ink-500">{row.reference ?? "—"}</td>
                <td className="text-ink-500">{row.recordedByName ?? "—"}</td>
                <td className="num tabular font-medium">
                  {fmt.money(row.amount)}
                  {row.unallocated > 0 ? (
                    <span className="block text-xs text-info-700">{fmt.money(row.unallocated)} credit</span>
                  ) : null}
                </td>
              </tr>
            ))}
          </Table>
        )}

        <Pagination meta={data?.page} params={list} noun={"payments"} />
      </Card>
    </div>
  );
}
